import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Person component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconPerson extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.0002 2.60498C9.61298 2.60498 7.67773 4.54023 7.67773 6.92748C7.67773 9.31473 9.61298 11.25 12.0002 11.25C14.3875 11.25 16.3227 9.31473 16.3227 6.92748C16.3227 4.54023 14.3875 2.60498 12.0002 2.60498ZM6.17773 6.92748C6.17773 3.7118 8.78456 1.10498 12.0002 1.10498C15.2159 1.10498 17.8227 3.7118 17.8227 6.92748C17.8227 10.1432 15.2159 12.75 12.0002 12.75C8.78456 12.75 6.17773 10.1432 6.17773 6.92748Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.58845 16.4489C7.45033 15.0907 9.69542 14.3589 12.0001 14.3589C14.3047 14.3589 16.5498 15.0907 18.4117 16.4489C20.2735 17.8071 21.656 19.7215 22.3598 21.9161C22.4329 22.1442 22.3927 22.3935 22.2515 22.5871C22.1103 22.7806 21.8852 22.8951 21.6456 22.8951H2.3545C2.11493 22.8951 1.88977 22.7806 1.74858 22.5871C1.60739 22.3935 1.56716 22.1442 1.64032 21.9161C2.34408 19.7215 3.72656 17.8071 5.58845 16.4489ZM12.0001 15.8589C10.0132 15.8589 8.07762 16.4898 6.47245 17.6608C5.15092 18.6248 4.10953 19.9138 3.44418 21.3951H20.5559C19.8906 19.9138 18.8492 18.6248 17.5277 17.6608C15.9225 16.4898 13.9869 15.8589 12.0001 15.8589Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-person') === undefined) {
  customElements.define('cat-icon-person', BlocksIconPerson);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-person': BlocksIconPerson;
  }
}
